import { useState, useEffect, useMemo } from "react";
import { FILTER_OPERATORS } from "@/components/tableau";

// Parse a date from French (DD/MM/YYYY), ISO (YYYY-MM-DD) or German (DD.MM.YYYY) formats
const parseDateFlexible = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);              // ISO (date input value)
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/);          // DD/MM/YYYY or DD.MM.YYYY
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

// Day-level timestamp (ignores time) for robust date comparisons
const dayTs = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();

const applyFilter = (value, operator, filterValue, columnType, filterValue2) => {
  const strValue = String(value || '').toLowerCase().trim();
  const strFilter = String(filterValue || '').toLowerCase().trim();
  switch (operator) {
    case 'contains': return strValue.includes(strFilter);
    case 'not_contains': return !strValue.includes(strFilter);
    case 'equals': return strValue === strFilter;
    case 'not_equals': return strValue !== strFilter;
    case 'starts_with': return strValue.startsWith(strFilter);
    case 'ends_with': return strValue.endsWith(strFilter);
    case 'greater_than': {
      if (columnType === 'date') { const d = parseDateFlexible(value), f = parseDateFlexible(filterValue); return !!(d && f) && dayTs(d) > dayTs(f); }
      const n1 = parseFloat(strValue.replace(/[^\d.,-]/g, '').replace(',', '.'));
      const n2 = parseFloat(strFilter.replace(/[^\d.,-]/g, '').replace(',', '.'));
      return !isNaN(n1) && !isNaN(n2) && n1 > n2;
    }
    case 'less_than': {
      if (columnType === 'date') { const d = parseDateFlexible(value), f = parseDateFlexible(filterValue); return !!(d && f) && dayTs(d) < dayTs(f); }
      const n1 = parseFloat(strValue.replace(/[^\d.,-]/g, '').replace(',', '.'));
      const n2 = parseFloat(strFilter.replace(/[^\d.,-]/g, '').replace(',', '.'));
      return !isNaN(n1) && !isNaN(n2) && n1 < n2;
    }
    case 'between': {
      const n = parseFloat(strValue.replace(/[^\d.,-]/g, '').replace(',', '.'));
      const lo = parseFloat(String(filterValue || '').replace(/[^\d.,-]/g, '').replace(',', '.'));
      const hi = parseFloat(String(filterValue2 || '').replace(/[^\d.,-]/g, '').replace(',', '.'));
      if (isNaN(n)) return false;
      if (!isNaN(lo) && n < lo) return false;
      if (!isNaN(hi) && n > hi) return false;
      return !isNaN(lo) || !isNaN(hi);
    }
    case 'date_equals': {
      const d = parseDateFlexible(value), f = parseDateFlexible(filterValue);
      return !!(d && f) && dayTs(d) === dayTs(f);
    }
    case 'date_before': {
      const d = parseDateFlexible(value), f = parseDateFlexible(filterValue);
      return !!(d && f) && dayTs(d) < dayTs(f);
    }
    case 'date_after': {
      const d = parseDateFlexible(value), f = parseDateFlexible(filterValue);
      return !!(d && f) && dayTs(d) > dayTs(f);
    }
    case 'date_between': {
      const d = parseDateFlexible(value);
      if (!d) return false;
      const f1 = parseDateFlexible(filterValue);
      const f2 = parseDateFlexible(filterValue2);
      const t = dayTs(d);
      if (f1 && t < dayTs(f1)) return false;
      if (f2 && t > dayTs(f2)) return false;
      return !!(f1 || f2);
    }
    case 'is_empty': return !strValue || strValue === '';
    case 'is_not_empty': return strValue && strValue !== '';
    default: return true;
  }
};

export function useFiltering(orders, columns, getOrderedColumns, getCellValue, availableStatuses, availableTypes) {
  const [globalSearch, setGlobalSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterRules, setFilterRules] = useState([]);
  const [filterLogic, setFilterLogic] = useState("and");
  const [statusFilter, setStatusFilter] = useState([]);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [statusFilterPosition, setStatusFilterPosition] = useState({ top: 0, right: 0 });
  const [typeFilter, setTypeFilter] = useState([]);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [typeFilterPosition, setTypeFilterPosition] = useState({ top: 0, right: 0 });

  // Sort state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Debounce global search (300ms) to avoid re-filtering on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(globalSearch), 300);
    return () => clearTimeout(t);
  }, [globalSearch]);

  // Close status filter on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusFilter && !event.target.closest('[data-testid="status-filter-btn"]') && !event.target.closest('.status-filter-dropdown')) {
        setShowStatusFilter(false);
      }
      if (showTypeFilter && !event.target.closest('[data-testid="type-filter-btn"]') && !event.target.closest('.type-filter-dropdown')) {
        setShowTypeFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusFilter, showTypeFilter]);

  const addFilterRule = () => {
    const cols = getOrderedColumns();
    const firstColumn = cols[0];
    setFilterRules(prev => [...prev, { id: Date.now(), column: firstColumn?.name || '', operator: 'contains', value: '' }]);
  };

  const updateFilterRule = (id, field, value) => {
    setFilterRules(prev => prev.map(rule => rule.id === id ? { ...rule, [field]: value } : rule));
  };

  const removeFilterRule = (id) => {
    setFilterRules(prev => prev.filter(rule => rule.id !== id));
  };

  const clearAllFilters = () => {
    setFilterRules([]);
    setGlobalSearch("");
    setStatusFilter([]);
    setTypeFilter([]);
  };

  const toggleStatusFilter = (status) => {
    setStatusFilter(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  const clearStatusFilter = () => setStatusFilter([]);
  const selectAllStatuses = () => setStatusFilter([...(availableStatuses || [])]);

  const toggleTypeFilter = (type) => {
    setTypeFilter(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const clearTypeFilter = () => setTypeFilter([]);
  const selectAllTypes = () => setTypeFilter([...(availableTypes || [])]);

  const isAnyFilterActive = statusFilter.length > 0 || typeFilter.length > 0 || globalSearch.trim() || filterRules.length > 0;
  const isStatusFilterActive = statusFilter.length > 0;
  const isTypeFilterActive = typeFilter.length > 0;

  const getOperatorsForColumn = (columnName) => {
    const col = columns.find(c => c.name === columnName);
    const colType = col?.type || 'text';
    return FILTER_OPERATORS.filter(op => op.types.includes(colType));
  };

  // Memoized ordered columns (stable identity)
  const orderedCols = useMemo(() => getOrderedColumns(), [getOrderedColumns]);

  // Memoized filtering: computed once per relevant change instead of on every render
  const filteredOrders = useMemo(() => {
    let filtered = orders;
    if (statusFilter.length > 0) {
      filtered = filtered.filter(order => {
        const extracted = order.extracted_data || {};
        const sv = extracted["order_status"] || extracted["ORDER STATUS"] || extracted["order.status"] || extracted["ORDER.STATUS"] || extracted["status"] || "";
        return statusFilter.includes(String(sv).trim());
      });
    }
    if (typeFilter.length > 0) {
      filtered = filtered.filter(order => {
        const extracted = order.extracted_data || {};
        const tv = extracted["TYPE"] || extracted["type"] || extracted["Type"] || "";
        return typeFilter.includes(String(tv).trim());
      });
    }
    if (debouncedSearch.trim()) {
      const searchLower = debouncedSearch.toLowerCase().trim();
      filtered = filtered.filter(order =>
        orderedCols.some(col => String(getCellValue(order, col)).toLowerCase().includes(searchLower)) ||
        Object.values(order.extracted_data || {}).some(v => v !== null && v !== undefined && String(v).toLowerCase().includes(searchLower))
      );
    }
    if (filterRules.length > 0) {
      filtered = filtered.filter(order => {
        const results = filterRules.map(rule => {
          const col = columns.find(c => c.name === rule.column);
          if (!col) return true;
          return applyFilter(getCellValue(order, col), rule.operator, rule.value, col.type || 'text', rule.value2);
        });
        return filterLogic === 'and' ? results.every(r => r) : results.some(r => r);
      });
    }
    return filtered;
  }, [orders, statusFilter, typeFilter, debouncedSearch, filterRules, filterLogic, columns, orderedCols, getCellValue]);

  // Memoized sorting
  const sortedOrders = useMemo(() => {
    if (!sortColumn) return filteredOrders;
    const col = columns.find(c => c.name === sortColumn);
    if (!col) return filteredOrders;
    const colType = col.type || 'text';
    return [...filteredOrders].sort((a, b) => {
      const valA = getCellValue(a, col);
      const valB = getCellValue(b, col);
      // Date sorting: parse DD/MM/YYYY or ISO dates
      if (colType === 'date') {
        const parseDate = (v) => {
          if (!v) return null;
          const ddmmyyyy = String(v).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (ddmmyyyy) return new Date(+ddmmyyyy[3], +ddmmyyyy[2] - 1, +ddmmyyyy[1]);
          const d = new Date(v);
          return isNaN(d.getTime()) ? null : d;
        };
        const dA = parseDate(valA);
        const dB = parseDate(valB);
        if (dA && dB) return sortDirection === "asc" ? dA - dB : dB - dA;
        if (dA && !dB) return sortDirection === "asc" ? -1 : 1;
        if (!dA && dB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }
      const numA = parseFloat(String(valA).replace(/[^\d.-]/g, ''));
      const numB = parseFloat(String(valB).replace(/[^\d.-]/g, ''));
      if (!isNaN(numA) && !isNaN(numB)) return sortDirection === "asc" ? numA - numB : numB - numA;
      const comparison = String(valA).localeCompare(String(valB), 'fr', { numeric: true, sensitivity: 'base' });
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredOrders, sortColumn, sortDirection, columns, getCellValue]);

  const handleSort = (columnName) => {
    if (sortColumn === columnName) {
      if (sortDirection === "asc") setSortDirection("desc");
      else { setSortColumn(null); setSortDirection("asc"); }
    } else {
      setSortColumn(columnName);
      setSortDirection("asc");
    }
  };

  return {
    globalSearch, setGlobalSearch,
    showAdvancedFilters, setShowAdvancedFilters,
    filterRules, filterLogic, setFilterLogic,
    statusFilter, showStatusFilter, setShowStatusFilter,
    statusFilterPosition, setStatusFilterPosition,
    typeFilter, showTypeFilter, setShowTypeFilter,
    typeFilterPosition, setTypeFilterPosition,
    sortColumn, sortDirection,
    setFilterRules,
    addFilterRule, updateFilterRule, removeFilterRule, clearAllFilters,
    toggleStatusFilter, clearStatusFilter, selectAllStatuses,
    toggleTypeFilter, clearTypeFilter, selectAllTypes,
    isAnyFilterActive, isStatusFilterActive, isTypeFilterActive,
    getOperatorsForColumn,
    filteredOrders, sortedOrders, handleSort,
  };
}
